import type { ILanguageResources, ITranslationModule } from '../../interfaces';
import { interpolate } from '../../global';

class Id {
  private constructor() {}
  public static readonly prefix = 'UI.scripts.purchaseOrders.PurchaseOrderDetailView.';
  public static readonly itemNumberTitle = `${Id.prefix}itemNumberTitle`;
  public static readonly imageTitle = `${Id.prefix}imageTitle`;
  public static readonly frameTitle = `${Id.prefix}frameTitle`;
  public static readonly frameTitleAndDescription = `${Id.prefix}frameTitleAndDescription`;
  public static readonly optionsTitle = `${Id.prefix}optionsTitle`;
  public static readonly notesToManufacturer = `${Id.prefix}notesToManufacturer`;
  public static readonly frameConfiguration = `${Id.prefix}frameConfiguration`;
  public static readonly information = `${Id.prefix}information`;
  public static readonly supplierNote = `${Id.prefix}supplierNote`;
  public static readonly warning = `${Id.prefix}warning`;
  public static readonly criticalError = `${Id.prefix}criticalError`;
  public static readonly requireValidation = `${Id.prefix}requireValidation`;
  public static readonly tags = `${Id.prefix}tags`;
  public static readonly quantityTitle = `${Id.prefix}quantityTitle`;
  public static readonly priceTitle = `${Id.prefix}priceTitle`;
  public static readonly reference = `${Id.prefix}reference`;
  public static readonly pleaseProvideReference = `${Id.prefix}pleaseProvideReference`;
  public static readonly orderTotal = `${Id.prefix}orderTotal`;

  // --- Delivery batches (MRD, Task 230006) ---
  public static readonly batchItemCount = `${Id.prefix}batchItemCount`;
  public static readonly batchItemCountOne = `${Id.prefix}batchItemCountOne`;
  public static readonly viewBatchDetails = `${Id.prefix}viewBatchDetails`;
  public static readonly batchDetailsTitle = `${Id.prefix}batchDetailsTitle`;
  public static readonly batchDescription = `${Id.prefix}batchDescription`;
  public static readonly batchSectionRequested = `${Id.prefix}batchSectionRequested`;
  public static readonly batchSectionConfirmed = `${Id.prefix}batchSectionConfirmed`;
  public static readonly batchDeliveryDate = `${Id.prefix}batchDeliveryDate`;
  public static readonly batchCustomerRef = `${Id.prefix}batchCustomerRef`;
  public static readonly batchComment = `${Id.prefix}batchComment`;
  public static readonly batchStatus = `${Id.prefix}batchStatus`;
  public static readonly batchLastUpdated = `${Id.prefix}batchLastUpdated`;
  public static readonly batchAwaitingSupplier = `${Id.prefix}batchAwaitingSupplier`;
  public static readonly batchNotProvided = `${Id.prefix}batchNotProvided`;
  public static readonly close = `${Id.prefix}close`;
  public static readonly batchTotal = `${Id.prefix}batchTotal`;
}

export class LangPurchaseOrderDetailView {
  private constructor() {}
  public static itemNumberTitle(): string {
    return interpolate(Id.itemNumberTitle);
  }
  public static imageTitle(): string {
    return interpolate(Id.imageTitle);
  }
  public static frameTitle(): string {
    return interpolate(Id.frameTitle);
  }
  public static frameTitleAndDescription(): string {
    return interpolate(Id.frameTitleAndDescription);
  }
  public static optionsTitle(): string {
    return interpolate(Id.optionsTitle);
  }
  public static notesToManufacturer(): string {
    return interpolate(Id.notesToManufacturer);
  }
  public static frameConfiguration(): string {
    return interpolate(Id.frameConfiguration);
  }
  public static information(): string {
    return interpolate(Id.information);
  }
  public static supplierNote(): string {
    return interpolate(Id.supplierNote);
  }
  public static warning(): string {
    return interpolate(Id.warning);
  }
  public static criticalError(): string {
    return interpolate(Id.criticalError);
  }
  public static requireValidation(): string {
    return interpolate(Id.requireValidation);
  }
  public static tags(): string {
    return interpolate(Id.tags);
  }
  public static quantityTitle(): string {
    return interpolate(Id.quantityTitle);
  }
  public static priceTitle(): string {
    return interpolate(Id.priceTitle);
  }
  public static reference(): string {
    return interpolate(Id.reference);
  }
  public static pleaseProvideReference(): string {
    return interpolate(Id.pleaseProvideReference);
  }
  public static orderTotal(): string {
    return interpolate(Id.orderTotal);
  }

  // --- Delivery batches (MRD, Task 230006) ---
  public static batchItemCount(params: { count: number }): string {
    return params.count === 1 ? interpolate(Id.batchItemCountOne, params) : interpolate(Id.batchItemCount, params);
  }
  public static viewBatchDetails(): string {
    return interpolate(Id.viewBatchDetails);
  }
  public static batchDetailsTitle(): string {
    return interpolate(Id.batchDetailsTitle);
  }
  public static batchDescription(): string {
    return interpolate(Id.batchDescription);
  }
  public static batchSectionRequested(): string {
    return interpolate(Id.batchSectionRequested);
  }
  public static batchSectionConfirmed(): string {
    return interpolate(Id.batchSectionConfirmed);
  }
  public static batchDeliveryDate(): string {
    return interpolate(Id.batchDeliveryDate);
  }
  public static batchCustomerRef(): string {
    return interpolate(Id.batchCustomerRef);
  }
  public static batchComment(): string {
    return interpolate(Id.batchComment);
  }
  public static batchStatus(): string {
    return interpolate(Id.batchStatus);
  }
  public static batchLastUpdated(): string {
    return interpolate(Id.batchLastUpdated);
  }
  public static batchAwaitingSupplier(): string {
    return interpolate(Id.batchAwaitingSupplier);
  }
  public static batchNotProvided(): string {
    return interpolate(Id.batchNotProvided);
  }
  public static close(): string {
    return interpolate(Id.close);
  }
  public static batchTotal(): string {
    return interpolate(Id.batchTotal);
  }
}

export class PurchaseOrderDetailViewModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(Id.itemNumberTitle, 'Item #', 'Title for item number column');
    register.add(Id.imageTitle, 'Image', 'Title for image column');
    register.add(Id.frameTitle, "{{glossary 'frame-title'}}", 'Frame title fallback');
    register.add(
      Id.frameTitleAndDescription,
      "{{glossary 'frame-title'}} and Description",
      'Column title for frame title and description'
    );
    register.add(Id.optionsTitle, 'Options', 'Options column title');
    register.add(Id.notesToManufacturer, 'Notes to Manufacturer', 'Notes to Manufacturer label');
    register.add(Id.frameConfiguration, 'Frame Configuration', 'Frame configuration label');
    register.add(Id.information, 'Information', 'Information label');
    register.add(Id.supplierNote, 'Supplier Note', 'Supplier Note label');
    register.add(Id.warning, 'Warning', 'Warning label');
    register.add(Id.criticalError, 'Critical Error', 'Critical Error label');
    register.add(Id.requireValidation, 'Require Validation', 'Require Validation label');
    register.add(Id.tags, 'Tags', 'Tags label');
    register.add(Id.quantityTitle, 'Qty', 'Quantity column title');
    register.add(Id.priceTitle, 'Price', 'Price column title');
    register.add(Id.reference, 'Reference', 'Reference label');
    register.add(Id.pleaseProvideReference, 'Please provide a reference', 'Prompt to provide a reference');
    register.add(
      Id.orderTotal,
      "{{glossary 'purchase-order'}} Total",
      'Label on the items table grand-total footer row'
    );

    // --- Delivery batches (MRD, Task 230006) ---
    register.add(Id.batchItemCount, '{{count}} items', 'Badge: number of items in a delivery batch (plural)');
    register.add(Id.batchItemCountOne, '{{count}} item', 'Badge: number of items in a delivery batch (singular)');
    register.add(
      Id.viewBatchDetails,
      "View {{glossary 'quote-batch'}} Details",
      'Menu item: open the read-only batch details dialog'
    );
    register.add(
      Id.batchDetailsTitle,
      "{{glossary 'quote-batch'}} Details",
      'Title of the read-only batch details dialog'
    );
    register.add(Id.batchDescription, 'Description', 'Field: batch description');
    register.add(
      Id.batchSectionRequested,
      'Requested',
      'Section heading: the dealer-authored (requested) batch values'
    );
    register.add(
      Id.batchSectionConfirmed,
      'Confirmed by Supplier',
      'Section heading: the values confirmed/synced back by the supplier system (V6)'
    );
    // "Delivery", not "Delivery Date" — the value beside it is visibly a date. Matches the quote and reports.
    register.add(Id.batchDeliveryDate, 'Delivery', 'Field: batch required delivery date');
    register.add(Id.batchCustomerRef, 'Customer Reference', 'Field: batch customer reference');
    register.add(Id.batchComment, 'Delivery Comment', 'Field: batch delivery comment');
    register.add(Id.batchStatus, 'Status', 'Field: batch status');
    register.add(Id.batchLastUpdated, 'Last Updated', 'Field: when the supplier last updated this batch');
    register.add(
      Id.batchAwaitingSupplier,
      'Awaiting supplier',
      'Placeholder shown when the supplier system has not yet confirmed this batch'
    );
    register.add(Id.batchNotProvided, '—', 'Placeholder shown when a batch field has no value');
    register.add(Id.close, 'Close', 'Button: close the batch details dialog');
    register.add(
      Id.batchTotal,
      "{{glossary 'quote-batch'}} Total",
      'Label on the per-batch total footer row of the items table'
    );
  }
}
